/**
 * RuleFormDialog — create and edit gate rule dialog.
 * US-006 Steps 2 + 3: validated form with DSL expression input.
 * WCAG 2.1 AA: focus trap via Radix Dialog, explicit labels via htmlFor,
 * aria-describedby on error messages, aria-required on required inputs.
 * Form fields are in RuleFormFields.tsx (split for 200-line limit).
 */
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { useCreateRule, useUpdateRule, type GateRuleResponse } from '@/hooks/useGateRules';
import { RuleFormFields, type RuleFormValues } from './RuleFormFields';
import type { RuleSeverity } from '@/types/rules';

const schema = z.object({
  rule_name: z.string().min(1, 'Rule name is required').max(200, 'Max 200 characters'),
  description: z.string().max(1000).optional(),
  severity: z.enum(['blocking', 'warning', 'info'] as const, {
    errorMap: () => ({ message: 'Severity is required' }),
  }),
  category: z.string().max(100).optional(),
  source: z.enum(['regulatory', 'internal_policy'] as const).default('regulatory'),
  applies_to_types: z.array(z.string()).default([]),
  condition_raw: z.string().min(1, 'Condition is required').refine(
    (val) => {
      try {
        const parsed: unknown = JSON.parse(val);
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
      } catch {
        return false;
      }
    },
    { message: 'Must be a valid non-empty JSON object' },
  ),
  sort_order: z.coerce.number().int().min(0, 'Must be 0 or greater').default(0),
  enabled: z.boolean().default(true),
});

interface RuleFormDialogProps {
  open: boolean;
  editTarget: GateRuleResponse | null;
  onClose: () => void;
}

export function RuleFormDialog({ open, editTarget, onClose }: RuleFormDialogProps) {
  const isEdit = editTarget !== null;
  const { mutate: createRule, isPending: isCreating } = useCreateRule();
  const { mutate: updateRule, isPending: isUpdating } = useUpdateRule();
  const isPending = isCreating || isUpdating;

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } =
    useForm<RuleFormValues>({
      resolver: zodResolver(schema),
      defaultValues: { enabled: true, sort_order: 0, severity: 'warning' },
    });

  useEffect(() => {
    if (open && editTarget) {
      reset({
        rule_name: editTarget.rule_name,
        description: editTarget.description ?? '',
        severity: editTarget.severity,
        category: editTarget.category ?? '',
        source: editTarget.source,
        applies_to_types: editTarget.applies_to_types ?? [],
        condition_raw: JSON.stringify(editTarget.condition, null, 2),
        sort_order: editTarget.sort_order,
        enabled: editTarget.enabled,
      });
    } else if (open && !editTarget) {
      reset({
        enabled: true, sort_order: 0, severity: 'warning', source: 'regulatory',
        applies_to_types: [], condition_raw: '{}',
      });
    }
  }, [open, editTarget, reset]);

  const onSubmit = (values: RuleFormValues) => {
    const condition = JSON.parse(values.condition_raw) as Record<string, unknown>;
    const base = {
      rule_name: values.rule_name,
      description: values.description || undefined,
      severity: values.severity as RuleSeverity,
      category: values.category || undefined,
      source: values.source ?? 'regulatory',
      applies_to_types: values.applies_to_types ?? [],
      condition,
      sort_order: values.sort_order,
      enabled: values.enabled,
    };

    if (editTarget) {
      updateRule({ id: editTarget.id, payload: base }, { onSuccess: onClose });
    } else {
      createRule(base, { onSuccess: onClose });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Gate Rule' : 'Create Gate Rule'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the rule fields. Changes take effect on the next pipeline run.'
              : 'Define a new rule for the Gate Agent to evaluate during contract analysis.'}
          </DialogDescription>
        </DialogHeader>

        <form
          id="rule-form"
          onSubmit={(e) => void handleSubmit(onSubmit)(e)}
          noValidate
        >
          <RuleFormFields
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
          />
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" form="rule-form" disabled={isPending} aria-busy={isPending}>
            {isPending
              ? isEdit ? 'Saving…' : 'Creating…'
              : isEdit ? 'Save Changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
